import {
  ModalDialog,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { type NewBillingMethodDialogViewModel } from '@/features/billing/profile/NewBillingMethodDialog/NewBillingMethodDialogViewModel'
import { Button } from '@/components/Button'
import { CardBillingMethodEditor } from '@/features/billing/profile/CardBillingMethodEditor'
import { useId } from 'react'
import { observer } from 'mobx-react-lite'
import { useActivation } from '@/hooks/view-model-hooks'
import { IsSkeletonContext } from '@/components/Skeleton'
import { ButtonGroup } from '@/components/ButtonGroup'

export interface NewBillingMethodDialogProps {
  vm: NewBillingMethodDialogViewModel
  testId?: string
  cancelButtonTestId?: string
  submitButtonTestId?: string
}

/**
 * The NewBillingMethodDialog
 */
export const NewBillingMethodDialog = observer(function NewBillingMethodDialog({
  vm,
  testId,
  cancelButtonTestId,
  submitButtonTestId,
}: NewBillingMethodDialogProps) {
  const formId = useId()
  useActivation(vm)

  return (
    <ModalDialog
      size="lg"
      isOpen={vm.modal.showing}
      onClose={vm.modal.close}
      isDismissable={!vm.cardBillingMethodEditorVm?.save.pending}
      isUsingDialogScrolling
      data-test={testId}
    >
      <ModalDialogHeader>
        <IsSkeletonContext value={!vm.cardBillingMethodEditorVm?.elementsReady}>
          <ModalDialogTitle>Add payment method</ModalDialogTitle>
          <ModalDialogDescription>
            Enter your details to add a credit/debit card.
          </ModalDialogDescription>
        </IsSkeletonContext>
      </ModalDialogHeader>
      <ModalDialogContent>
        {vm.cardBillingMethodEditorVm && (
          <CardBillingMethodEditor
            compact
            formId={formId}
            vm={vm.cardBillingMethodEditorVm}
            isDisabled={vm.save.pending}
            onSubmit={vm.save}
          />
        )}
      </ModalDialogContent>
      <ModalDialogFooter bordered compact sticky>
        <ButtonGroup stretch>
          <Button
            variant="outline"
            isDisabled={vm.cardBillingMethodEditorVm?.save.pending}
            label="Cancel"
            onPress={vm.modal.close}
            testId={cancelButtonTestId}
          />
          <Button
            type="submit"
            form={formId}
            isDisabled={!vm.cardBillingMethodEditorVm?.elementsReady}
            isPending={vm.cardBillingMethodEditorVm?.save.pending}
            label="Add payment method"
            testId={submitButtonTestId}
          />
        </ButtonGroup>
      </ModalDialogFooter>
    </ModalDialog>
  )
})
